import React, { useEffect, useState } from "react";
import { Usuario } from "../types";
import { X, Users, UserPlus, Shield, User, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { fetchWithAuth } from "../services/api";

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserManagementModal: React.FC<UserManagementModalProps> = ({ isOpen, onClose }) => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [role, setRole] = useState<"ADMIN" | "OPERATOR">("OPERATOR");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: "sucesso" | "erro"; msg: string } | null>(null);

  const carregarUsuarios = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth("/api/auth/users");
      if (res.ok) {
        const data = await res.json();
        setUsuarios(data.usuarios || []);
      }
    } catch (err) {
      console.error("Erro ao listar equipe:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      carregarUsuarios();
      setFeedback(null);
    }
  }, [isOpen]);

  const handleCriarUsuario = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);

    if (!nome || !email || !senha) {
      setFeedback({ tipo: "erro", msg: "Preencha todos os campos do formulário." });
      return;
    }

    if (senha.length < 8) {
      setFeedback({ tipo: "erro", msg: "A senha deve ter no mínimo 8 caracteres." });
      return;
    }

    try {
      setSaving(true);
      const res = await fetchWithAuth("/api/auth/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome, email, senha, role }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setFeedback({ tipo: "sucesso", msg: `Operador "${nome}" cadastrado com sucesso!` });
        setNome("");
        setEmail("");
        setSenha("");
        setRole("OPERATOR");
        carregarUsuarios();
      } else {
        setFeedback({ tipo: "erro", msg: data.error || "Falha ao cadastrar usuário." });
      }
    } catch (err) {
      setFeedback({ tipo: "erro", msg: "Erro ao comunicar com o servidor." });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        style={{ maxWidth: "680px", maxHeight: "88vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Users size={22} color="#3b82f6" />
            <h2 style={{ fontSize: "1.2rem", fontWeight: 700, margin: 0 }}>
              Gerenciamento de Equipe & Acessos
            </h2>
          </div>
          <button className="btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Feedback */}
          {feedback && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 14px",
                borderRadius: "8px",
                fontSize: "0.85rem",
                background:
                  feedback.tipo === "sucesso"
                    ? "rgba(16, 185, 129, 0.15)"
                    : "rgba(239, 68, 68, 0.15)",
                border: `1px solid ${
                  feedback.tipo === "sucesso"
                    ? "rgba(16, 185, 129, 0.4)"
                    : "rgba(239, 68, 68, 0.4)"
                }`,
                color: feedback.tipo === "sucesso" ? "#6ee7b7" : "#fca5a5",
              }}
            >
              {feedback.tipo === "sucesso" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              <span>{feedback.msg}</span>
            </div>
          )}

          {/* Formulário Novo Operador */}
          <div
            style={{
              background: "var(--bg-secondary)",
              border: "1px solid var(--border-color)",
              borderRadius: "12px",
              padding: "18px 20px",
            }}
          >
            <h3
              style={{
                fontSize: "0.95rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "14px",
                color: "#f8fafc",
              }}
            >
              <UserPlus size={18} color="#10b981" /> Convidar / Cadastrar Novo Operador
            </h3>

            <form onSubmit={handleCriarUsuario} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Ex: Carlos Silva"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>
                    E-mail
                  </label>
                  <input
                    type="email"
                    className="search-input"
                    placeholder="carlos@empresa.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>
                    Senha Inicial (mínimo 8 caracteres)
                  </label>
                  <input
                    type="password"
                    className="search-input"
                    placeholder="••••••••"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>
                    Papel / Permissão
                  </label>
                  <select
                    className="select-custom"
                    style={{ width: "100%", height: "40px" }}
                    value={role}
                    onChange={(e) => setRole(e.target.value as "ADMIN" | "OPERATOR")}
                  >
                    <option value="OPERATOR">Operador (Consulta & Simulações)</option>
                    <option value="ADMIN">Administrador (Acesso Total)</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ marginTop: "6px", alignSelf: "flex-end", display: "flex", alignItems: "center", gap: "6px" }}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                Cadastrar Operador
              </button>
            </form>
          </div>

          {/* Lista de Usuários Existentes */}
          <div>
            <h3 style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--text-muted)", marginBottom: "12px" }}>
              Usuários Autorizados no Sistema ({usuarios.length})
            </h3>

            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                <Loader2 size={24} className="animate-spin" color="#3b82f6" />
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {usuarios.map((u) => (
                  <div
                    key={u.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      background: "var(--bg-card)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "8px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {u.role === "ADMIN" ? (
                        <Shield size={18} color="#f59e0b" />
                      ) : (
                        <User size={18} color="#3b82f6" />
                      )}
                      <div>
                        <div style={{ fontWeight: 600, color: "#f8fafc" }}>{u.nome}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{u.email}</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span
                        className="badge"
                        style={{
                          background: u.role === "ADMIN" ? "rgba(245, 158, 11, 0.2)" : "rgba(59, 130, 246, 0.2)",
                          color: u.role === "ADMIN" ? "#fbbf24" : "#60a5fa",
                        }}
                      >
                        {u.role}
                      </span>
                      <span
                        className="badge"
                        style={{
                          background: u.ativo ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)",
                          color: u.ativo ? "#34d399" : "#f87171",
                        }}
                      >
                        {u.ativo ? "Ativo" : "Inativo"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
